import { batch } from 'react-redux';
import { toast } from 'react-toastify';

import { useAppDispatch } from 'redux/store/store.types';
import { getApiErrorMessage } from 'utils';

import {
  useAddPackageMutation,
  useConfirmPackagePaymentMutation,
  useLazyFetchPackagesQuery,
  useUpdatePackageMutation,
} from '../api/eecs-package.api';
import { EecsPackageAC, setEecsPackageModal, setSelectedEecsPackage } from '../store/eecs-package.slice';
import {
  AddPackagePayload,
  ConfirmEecsPackagePaymentPayload,
  FetchPackagesPayload,
  IEecsPackage,
  IEecsPackageModal,
  UpdatePackagePayload,
} from '../types/eecs-package.types';

interface OpenModal {
  modal: IEecsPackageModal;
  eecsPackage: IEecsPackage | null;
}

interface UseEecsPackage {
  fetchPackagesHandler: (payload: FetchPackagesPayload) => Promise<boolean>;
  clearPackageState: () => void;
  openPackageModal: (payload: OpenModal) => void;
  closePackageModal: () => void;
  updatePackageHandler: (payload: UpdatePackagePayload) => Promise<boolean>;
  confirmPackagePaymentHandler: (payload: ConfirmEecsPackagePaymentPayload) => Promise<boolean>;
  addPackageHandler: (payload: AddPackagePayload) => Promise<boolean>;
}

export const useEecsPackage = (): UseEecsPackage => {
  const dispatch = useAppDispatch();

  const [fetchPackagesQuery] = useLazyFetchPackagesQuery();
  const [updatePackageQuery] = useUpdatePackageMutation();
  const [addPackageQuery] = useAddPackageMutation();
  const [confirmPackagePaymentQuery] = useConfirmPackagePaymentMutation();

  const confirmPackagePaymentHandler = async (payload: ConfirmEecsPackagePaymentPayload) => {
    const { error } = await confirmPackagePaymentQuery(payload);
    const isError = !!error;
    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to confirm EECS Package payment!');
    }
    return !isError;
  };

  const fetchPackagesHandler = async (payload: FetchPackagesPayload) => {
    dispatch(EecsPackageAC.toggleLoading(true));
    const { data: response, error } = await fetchPackagesQuery(payload);
    dispatch(EecsPackageAC.toggleLoading(false));
    const isError = error || !response?.data;
    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to get Eecs Packages!');
    } else {
      batch(() => {
        dispatch(EecsPackageAC.setItems(response.data));
        dispatch(EecsPackageAC.setPaginationMeta(response.meta));
      });
    }

    return !isError;
  };

  const addPackageHandler = async (payload: AddPackagePayload) => {
    dispatch(EecsPackageAC.toggleLoading(true));
    const { error } = await addPackageQuery(payload);
    dispatch(EecsPackageAC.toggleLoading(false));
    const isError = error;
    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to add Eecs Package!');
    }
    return !isError;
  };

  const updatePackageHandler = async (payload: UpdatePackagePayload) => {
    dispatch(EecsPackageAC.toggleLoading(true));
    const { data: response, error } = await updatePackageQuery(payload);
    dispatch(EecsPackageAC.toggleLoading(false));
    const isError = error || !response?.data;
    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to update Eecs Package!');
    }
    return !isError;
  };

  const openPackageModal = ({ modal, eecsPackage }: OpenModal) => {
    batch(() => {
      dispatch(setSelectedEecsPackage(eecsPackage));
      dispatch(setEecsPackageModal(modal));
    });
  };

  const closePackageModal = () => {
    batch(() => {
      dispatch(setSelectedEecsPackage(null));
      dispatch(setEecsPackageModal(null));
    });
  };

  const clearPackageState = () => {
    batch(() => {
      dispatch(EecsPackageAC.clearItems());
      dispatch(EecsPackageAC.clearFilters());
      dispatch(EecsPackageAC.clearSorts());
      dispatch(EecsPackageAC.setCurrentPage(1));
      dispatch(setSelectedEecsPackage(null));
      dispatch(setEecsPackageModal(null));
    });
  };

  return {
    fetchPackagesHandler,
    clearPackageState,
    openPackageModal,
    closePackageModal,
    updatePackageHandler,
    confirmPackagePaymentHandler,
    addPackageHandler,
  };
};
